import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { incomingInvoiceApi, GetIncomingInvoicesParams } from '../services/incomingInvoices'
import {
  IncomingInvoice,
  IncomingInvoiceCreate,
  IncomingInvoiceStatus,
  IncomingInvoiceUpdate,
} from '../types/incomingInvoice'
import { clientKeys } from './useClients'

export const incomingInvoiceKeys = {
  all: ['incoming-invoices'] as const,
  lists: () => [...incomingInvoiceKeys.all, 'list'] as const,
  list: (params?: GetIncomingInvoicesParams) => [...incomingInvoiceKeys.lists(), params] as const,
  details: () => [...incomingInvoiceKeys.all, 'detail'] as const,
  detail: (id: number) => [...incomingInvoiceKeys.details(), id] as const,
}

export function useIncomingInvoicesList(params?: GetIncomingInvoicesParams) {
  return useQuery({
    queryKey: incomingInvoiceKeys.list(params),
    queryFn: () => incomingInvoiceApi.getAll(params),
  })
}

export function useIncomingInvoice(id: number) {
  return useQuery({
    queryKey: incomingInvoiceKeys.detail(id),
    queryFn: () => incomingInvoiceApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateIncomingInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: IncomingInvoiceCreate) => incomingInvoiceApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incomingInvoiceKeys.lists() })
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
    },
  })
}

export function useUpdateIncomingInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: IncomingInvoiceUpdate }) =>
      incomingInvoiceApi.update(id, data),
    onSuccess: (updated: IncomingInvoice) => {
      queryClient.setQueryData(incomingInvoiceKeys.detail(updated.id), updated)
      queryClient.invalidateQueries({ queryKey: incomingInvoiceKeys.lists() })
    },
  })
}

export function useUpdateIncomingInvoiceStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: IncomingInvoiceStatus }) =>
      incomingInvoiceApi.updateStatus(id, status),
    onSuccess: (updated: IncomingInvoice) => {
      queryClient.setQueryData(incomingInvoiceKeys.detail(updated.id), updated)
      queryClient.invalidateQueries({ queryKey: incomingInvoiceKeys.lists() })
    },
  })
}

export function useAnnulIncomingInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => incomingInvoiceApi.annul(id),
    onSuccess: (updated: IncomingInvoice) => {
      queryClient.setQueryData(incomingInvoiceKeys.detail(updated.id), updated)
      queryClient.invalidateQueries({ queryKey: incomingInvoiceKeys.lists() })
    },
  })
}

export function useUploadIncomingInvoiceFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ invoiceId, file }: { invoiceId: number; file: File }) =>
      incomingInvoiceApi.uploadFile(invoiceId, file),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: incomingInvoiceKeys.detail(invoiceId) })
    },
  })
}

export function useDeleteIncomingInvoiceFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ invoiceId, fileId }: { invoiceId: number; fileId: number }) =>
      incomingInvoiceApi.deleteFile(invoiceId, fileId),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: incomingInvoiceKeys.detail(invoiceId) })
    },
  })
}
